import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  type BoxType,
  type FloorArea,
  type StackingSettings,
  calculateLayout,
  renderTopViewSVG,
  renderSideViewSVG,
  fmtDim,
  genId,
  buildConfig,
  parseAndValidateConfig,
  DEFAULT_SETTINGS,
  DEFAULT_BOX_TYPES,
  DEFAULT_FLOOR_AREAS,
  NEW_BOX_DEFAULTS,
  NEW_FLOOR_AREA_DEFAULTS,
  BOX_COLORS,
} from "./spaceCalculatorEngine";
import "./SpaceCalculator.css";

function getBoxColor(index: number): string {
  return BOX_COLORS[index % BOX_COLORS.length];
}

function buildInitialBoxTypes(): BoxType[] {
  return DEFAULT_BOX_TYPES.map((bt, i) => ({ id: "bt" + (i + 1), ...bt }));
}

function buildInitialFloorAreas(boxTypes: BoxType[]): FloorArea[] {
  return DEFAULT_FLOOR_AREAS.map((fa, i) => ({
    id: "fa" + (i + 1),
    name: fa.name,
    length: fa.length,
    width: fa.width,
    height: fa.height,
    boxTypeId: boxTypes[fa.boxTypeIndex]?.id ?? boxTypes[0]?.id ?? "",
    needsAisle: fa.needsAisle,
  }));
}

// ─── Sub-components ──────────────────────────────────────────────

interface SettingsPanelProps {
  settings: StackingSettings;
  onChange: (patch: Partial<StackingSettings>) => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ settings, onChange }) => (
  <section className="sc-card" id="sc-settings-panel">
    <h2>Stacking Parameters</h2>
    <div className="sc-settings-grid">
      <div className="sc-field">
        <label>Aisle Width</label>
        <div className="sc-input-with-unit">
          <input type="number" step={1} min={12} max={120} value={settings.aisleWidth}
            onChange={(e) => onChange({ aisleWidth: parseFloat(e.target.value) || 0 })} />
          <span className="sc-unit">in</span>
        </div>
      </div>
      <div className="sc-field">
        <label>Max Stack Height</label>
        <div className="sc-input-with-unit">
          <input type="number" step={1} min={1} max={30} value={settings.maxStackHeight}
            onChange={(e) => onChange({ maxStackHeight: parseInt(e.target.value) || 0 })} />
          <span className="sc-unit">boxes</span>
        </div>
      </div>
      <div className="sc-field">
        <label>Stability Ratio</label>
        <div className="sc-input-with-unit">
          <input type="number" step={0.1} min={1} max={10} value={settings.stabilityRatio}
            onChange={(e) => onChange({ stabilityRatio: parseFloat(e.target.value) || 0 })} />
          <span className="sc-unit">: 1</span>
        </div>
        <span className="sc-hint">Max stack height ÷ row width</span>
      </div>
      <div className="sc-field">
        <label>Row Depth</label>
        <div className="sc-input-with-unit">
          <input type="number" step={1} min={1} max={20} value={settings.rowDepth}
            onChange={(e) => onChange({ rowDepth: parseInt(e.target.value) || 0 })} />
          <span className="sc-unit">rows</span>
        </div>
        <span className="sc-hint">Rows between aisles; only front row needs access</span>
      </div>
    </div>
  </section>
);

interface BoxTypesPanelProps {
  boxTypes: BoxType[];
  onUpdate: (id: string, patch: Partial<BoxType>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
}

const BoxTypesPanel: React.FC<BoxTypesPanelProps> = ({ boxTypes, onUpdate, onAdd, onRemove }) => (
  <section className="sc-card" id="sc-box-types-panel">
    <h2>Box Types</h2>
    <div className="sc-box-type-header">
      <span>Name</span><span>L (in)</span><span>W (in)</span><span>H (in)</span><span>Wt (oz)</span><span>Qty needed</span><span></span>
    </div>
    {boxTypes.map((bt, i) => {
      const color = getBoxColor(i);
      return (
        <div key={bt.id} className="sc-box-type-row">
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span className="sc-color-swatch" style={{ background: color }} />
            <input type="text" value={bt.name}
              onChange={(e) => onUpdate(bt.id, { name: e.target.value })} />
          </div>
          <input type="number" value={bt.length} step={0.25} min={0}
            onChange={(e) => onUpdate(bt.id, { length: parseFloat(e.target.value) || 0 })} />
          <input type="number" value={bt.width} step={0.25} min={0}
            onChange={(e) => onUpdate(bt.id, { width: parseFloat(e.target.value) || 0 })} />
          <input type="number" value={bt.height} step={0.25} min={0}
            onChange={(e) => onUpdate(bt.id, { height: parseFloat(e.target.value) || 0 })} />
          <input type="number" value={bt.weight} step={1} min={0}
            onChange={(e) => onUpdate(bt.id, { weight: parseFloat(e.target.value) || 0 })} />
          <input type="number" value={bt.quantity} step={1} min={0}
            onChange={(e) => onUpdate(bt.id, { quantity: parseInt(e.target.value) || 0 })} />
          <button className="sc-btn-remove" onClick={() => onRemove(bt.id)}
            disabled={boxTypes.length <= 1} title="Remove">&times;</button>
        </div>
      );
    })}
    <button className="sc-btn-add" onClick={onAdd}>+ Add Box Type</button>
  </section>
);

interface SummaryRow {
  bt: BoxType;
  color: string;
  capacity: number;
  assignedAreas: string;
}

interface SummaryPanelProps {
  rows: SummaryRow[];
}

const SummaryPanel: React.FC<SummaryPanelProps> = ({ rows }) => {
  let totalNeeded = 0;
  let totalCapacity = 0;
  rows.forEach((r) => {
    totalNeeded += r.bt.quantity;
    totalCapacity += r.capacity;
  });
  const totalDiff = totalCapacity - totalNeeded;

  return (
    <section className="sc-summary-section">
      <div className="sc-card">
        <h2>Inventory Summary</h2>
        <table className="sc-summary-table">
          <thead>
            <tr><th>Box Type</th><th>Needed</th><th>Assigned Areas</th><th>Capacity</th><th>Status</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const diff = r.capacity - r.bt.quantity;
              return (
                <tr key={r.bt.id}>
                  <td><span className="sc-color-swatch" style={{ background: r.color }} />{r.bt.name}</td>
                  <td className="sc-num">{r.bt.quantity.toLocaleString()}</td>
                  <td>{r.assignedAreas || "—"}</td>
                  <td className="sc-num">{r.capacity.toLocaleString()}</td>
                  <td>{diff >= 0
                    ? <span className="sc-tag-ok">+{diff} surplus</span>
                    : <span className="sc-tag-short">{diff} short</span>}
                  </td>
                </tr>
              );
            })}
            <tr className="sc-summary-total-row">
              <td><strong>Total</strong></td>
              <td className="sc-num"><strong>{totalNeeded.toLocaleString()}</strong></td>
              <td></td>
              <td className="sc-num"><strong>{totalCapacity.toLocaleString()}</strong></td>
              <td>{totalDiff >= 0
                ? <span className="sc-tag-ok">+{totalDiff} surplus</span>
                : <span className="sc-tag-short">{totalDiff} short</span>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
};

interface AreaResultProps {
  floorArea: FloorArea;
  boxTypes: BoxType[];
  settings: StackingSettings;
}

const AreaResult: React.FC<AreaResultProps> = ({ floorArea, boxTypes, settings }) => {
  const bt = boxTypes.find((b) => b.id === floorArea.boxTypeId);
  if (!bt) return <div className="sc-area-no-result">Select a box type to see the stacking layout.</div>;

  const layout = calculateLayout(floorArea, bt, settings);
  if (!layout || layout.totalBoxes === 0)
    return <div className="sc-area-no-result">This area is too small to fit any boxes with the current settings.</div>;

  const btIndex = boxTypes.indexOf(bt);
  const color = getBoxColor(btIndex);

  const weightPerBox = bt.weight / 16;
  const weightPerStack = weightPerBox * layout.stackHeight;
  const totalWeight = weightPerBox * layout.totalBoxes;
  const floorAreaSqft = floorArea.length * floorArea.width;
  const usedSqIn = layout.usedAlong * (layout.rows.length > 0 ? layout.rows[layout.rows.length - 1].perpEnd : 0);
  const totalSqIn = layout.floorAlong * layout.floorPerp;
  const utilPct = totalSqIn > 0 ? Math.round(usedSqIn / totalSqIn * 100) : 0;

  const aislesOnL = layout.aisleDir === "length";
  const aisleRunDir = aislesOnL ? `along ${fmtDim(floorArea.length)}` : `along ${fmtDim(floorArea.width)}`;
  const boxOrientDesc = `${layout.boxAlong}" × ${layout.boxPerp}" footprint`;
  const depthDesc = layout.unitRows === 2 ? "double-deep (back-to-back)" : "single-deep";

  const altCapacities = boxTypes.length > 1
    ? boxTypes.map((b, i) => {
        const l = calculateLayout(floorArea, b, settings);
        return { bt: b, capacity: l?.totalBoxes ?? 0, color: getBoxColor(i), isCurrent: b.id === bt.id };
      })
    : null;

  return (
    <div className="sc-area-result-grid">
      <div>
        <div className="sc-views-row">
          <div className="sc-svg-container">
            <div className="sc-view-label">Plan View</div>
            <div dangerouslySetInnerHTML={{ __html: renderTopViewSVG(layout, color) }} />
          </div>
          <div className="sc-side-view-container">
            <div className="sc-view-label">Side View</div>
            <div dangerouslySetInnerHTML={{ __html: renderSideViewSVG(layout, color) }} />
          </div>
        </div>
        {altCapacities && (
          <div className="sc-alt-capacities">
            <strong>Capacity comparison:</strong>{" "}
            {altCapacities.map((a) => (
              <span key={a.bt.id} className={a.isCurrent ? "sc-alt-current" : ""}>
                <span className="sc-color-swatch" style={{ background: a.color }} />
                {a.bt.name}: {a.capacity}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="sc-area-result-stats">
        <div className="sc-stat-primary">{layout.totalBoxes.toLocaleString()} <span className="sc-stat-unit">boxes</span></div>
        <div className="sc-stat-sub">{layout.totalStacks} stacks × {layout.stackHeight} high</div>
        <div className="sc-stats-detail-grid">
          <StatItem label="Rows" value={String(layout.totalRows)} />
          <StatItem label="Per row" value={String(layout.boxesPerRow)} />
          <StatItem label="Stack height" value={`${layout.stackHeight * layout.boxH}"`} />
          <StatItem label="Aisles" value={`${layout.aisles.length} × ${layout.aisleWidth}"`} />
          <StatItem label="Wt / stack" value={`${weightPerStack.toFixed(1)} lbs`} />
          <StatItem label="Total weight" value={`${totalWeight.toFixed(0)} lbs`} />
          <StatItem label="Floor area" value={`${floorAreaSqft} sq ft`} />
          <StatItem label="Utilization" value={`${utilPct}%`} />
        </div>
        <div className="sc-layout-desc">
          <strong>Arrangement:</strong> {depthDesc} rows, aisles run {aisleRunDir}.
          Box footprint: {boxOrientDesc}, stacked {layout.stackHeight} high ({layout.stackHeight * layout.boxH}").
          {(layout.wastedAlong > 0 || layout.wastedPerp > 0) &&
            ` Unused margins: ${layout.wastedAlong.toFixed(1)}" × ${layout.wastedPerp.toFixed(1)}".`}
          {" "}Stability limit: {layout.stabilityLimit} high. Ceiling limit: {layout.physicalLimit} high.
        </div>
      </div>
    </div>
  );
};

const StatItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="sc-stat-item">
    <span className="sc-stat-label">{label}</span>
    <span className="sc-stat-value">{value}</span>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────

const SpaceCalculator: React.FC = () => {
  const [settings, setSettings] = useState<StackingSettings>({ ...DEFAULT_SETTINGS });
  const [boxTypes, setBoxTypes] = useState<BoxType[]>(buildInitialBoxTypes);
  const [floorAreas, setFloorAreas] = useState<FloorArea[]>(() => buildInitialFloorAreas(buildInitialBoxTypes()));

  const handleSettingsChange = useCallback((patch: Partial<StackingSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleBoxTypeUpdate = useCallback((id: string, patch: Partial<BoxType>) => {
    setBoxTypes((prev) => prev.map((bt) => (bt.id === id ? { ...bt, ...patch } : bt)));
  }, []);

  const handleBoxTypeAdd = useCallback(() => {
    setBoxTypes((prev) => [...prev, { id: genId(), ...NEW_BOX_DEFAULTS }]);
  }, []);

  const handleBoxTypeRemove = useCallback((id: string) => {
    setBoxTypes((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((b) => b.id !== id);
      setFloorAreas((fas) =>
        fas.map((fa) => (fa.boxTypeId === id ? { ...fa, boxTypeId: next[0].id } : fa)),
      );
      return next;
    });
  }, []);

  const handleFloorAreaUpdate = useCallback((id: string, patch: Partial<FloorArea>) => {
    setFloorAreas((prev) => prev.map((fa) => (fa.id === id ? { ...fa, ...patch } : fa)));
  }, []);

  const handleFloorAreaAdd = useCallback((position: "top" | "bottom") => {
    const newArea: FloorArea = {
      id: genId(),
      name: `Area ${floorAreas.length + 1}`,
      ...NEW_FLOOR_AREA_DEFAULTS,
      boxTypeId: boxTypes[0]?.id ?? "",
      needsAisle: true,
    };
    setFloorAreas((prev) =>
      position === "top" ? [newArea, ...prev] : [...prev, newArea],
    );
  }, [boxTypes, floorAreas.length]);

  const handleFloorAreaRemove = useCallback((id: string) => {
    setFloorAreas((prev) => prev.filter((fa) => fa.id !== id));
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const handleExportConfig = useCallback(() => {
    const config = buildConfig(settings, boxTypes, floorAreas);
    const json = JSON.stringify(config, null, 2);
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const filename = `space-calc-config-${stamp}.json`;
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [settings, boxTypes, floorAreas]);

  const handleImportConfig = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setImportError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = parseAndValidateConfig(reader.result as string);
      if (!result.ok) {
        setImportError(result.error);
        return;
      }
      setSettings(result.config.settings);
      setBoxTypes(result.config.boxTypes);
      setFloorAreas(result.config.floorAreas);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const summaryRows = useMemo((): SummaryRow[] => {
    const capacityByType: Record<string, number> = {};
    boxTypes.forEach((bt) => { capacityByType[bt.id] = 0; });
    floorAreas.forEach((fa) => {
      const bt = boxTypes.find((b) => b.id === fa.boxTypeId);
      if (!bt) return;
      const layout = calculateLayout(fa, bt, settings);
      if (layout) capacityByType[bt.id] += layout.totalBoxes;
    });
    return boxTypes.map((bt, i) => ({
      bt,
      color: getBoxColor(i),
      capacity: capacityByType[bt.id] ?? 0,
      assignedAreas: floorAreas
        .filter((fa) => fa.boxTypeId === bt.id)
        .map((fa) => fa.name)
        .join(", "),
    }));
  }, [boxTypes, floorAreas, settings]);

  return (
    <div className="sc-root">
      <header className="sc-header">
        <div className="sc-header-inner">
          <div className="sc-header-top-row">
            <div>
              <p className="sc-subtitle">Plan box stacking layouts with aisle spacing and stability analysis</p>
            </div>
            <div className="sc-config-buttons">
              <button className="sc-btn-config" onClick={handleExportConfig}>Export Config</button>
              <button className="sc-btn-config" onClick={() => fileInputRef.current?.click()}>Load Config</button>
              <input ref={fileInputRef} type="file" accept=".json" className="sc-hidden-input"
                onChange={handleImportConfig} />
            </div>
          </div>
          {importError && (
            <div className="sc-import-error">
              <strong>Import failed:</strong> {importError}
              <button className="sc-import-error-dismiss" onClick={() => setImportError(null)}>&times;</button>
            </div>
          )}
        </div>
      </header>

      <main className="sc-main">
        <div className="sc-top-panels">
          <SettingsPanel settings={settings} onChange={handleSettingsChange} />
          <BoxTypesPanel boxTypes={boxTypes} onUpdate={handleBoxTypeUpdate}
            onAdd={handleBoxTypeAdd} onRemove={handleBoxTypeRemove} />
        </div>

        <SummaryPanel rows={summaryRows} />

        <section className="sc-areas-section">
          <div className="sc-section-header">
            <h2>Floor Areas</h2>
            <button className="sc-btn-add" onClick={() => handleFloorAreaAdd("top")}>+ Add Floor Area</button>
          </div>
          {floorAreas.map((fa) => (
            <div key={fa.id} className="sc-area-card">
              <div className="sc-area-card-header">
                <div className="sc-field sc-name-field">
                  <label>Name</label>
                  <input type="text" value={fa.name}
                    onChange={(e) => handleFloorAreaUpdate(fa.id, { name: e.target.value })} />
                </div>
                <div className="sc-field sc-dim-field">
                  <label>Length</label>
                  <div className="sc-input-with-unit">
                    <input type="number" value={fa.length} step={0.5} min={0}
                      onChange={(e) => handleFloorAreaUpdate(fa.id, { length: parseFloat(e.target.value) || 0 })} />
                    <span className="sc-unit">ft</span>
                  </div>
                </div>
                <div className="sc-field sc-dim-field">
                  <label>Width</label>
                  <div className="sc-input-with-unit">
                    <input type="number" value={fa.width} step={0.5} min={0}
                      onChange={(e) => handleFloorAreaUpdate(fa.id, { width: parseFloat(e.target.value) || 0 })} />
                    <span className="sc-unit">ft</span>
                  </div>
                </div>
                <div className="sc-field sc-dim-field">
                  <label>Height</label>
                  <div className="sc-input-with-unit">
                    <input type="number" value={fa.height} step={0.5} min={0}
                      onChange={(e) => handleFloorAreaUpdate(fa.id, { height: parseFloat(e.target.value) || 0 })} />
                    <span className="sc-unit">ft</span>
                  </div>
                </div>
                <div className="sc-field sc-assign-field">
                  <label>Box Type</label>
                  <select value={fa.boxTypeId}
                    onChange={(e) => handleFloorAreaUpdate(fa.id, { boxTypeId: e.target.value })}>
                    {boxTypes.map((bt) => (
                      <option key={bt.id} value={bt.id}>{bt.name}</option>
                    ))}
                  </select>
                </div>
                <div className="sc-field sc-aisle-field">
                  <label className="sc-checkbox-label">
                    <input type="checkbox" checked={fa.needsAisle}
                      onChange={(e) => handleFloorAreaUpdate(fa.id, { needsAisle: e.target.checked })} />
                    <span>Needs Aisle</span>
                  </label>
                </div>
                <button className="sc-btn-remove" onClick={() => handleFloorAreaRemove(fa.id)} title="Remove">&times;</button>
              </div>
              <div className="sc-area-card-body">
                <AreaResult floorArea={fa} boxTypes={boxTypes} settings={settings} />
              </div>
            </div>
          ))}
          <button className="sc-btn-add sc-btn-add-bottom" onClick={() => handleFloorAreaAdd("bottom")}>+ Add Floor Area</button>
        </section>
      </main>
    </div>
  );
};

export default SpaceCalculator;
